import type { ClusterDiagramNodeData } from 'stubben-edge-unified-frontend-react-components';
import type {
  InsightBulkPipelineResponse,
  InsightCompaniesPageData,
  InsightMapFilterConfig,
  InsightSectorCard,
  InsightSegment,
  InsightSegmentMenuItem
} from '@/interfaces/AudienceInsights.interface';
import type {
  InsightDrawerPayload,
  InsightHorizonItem,
  InsightHorizonPage,
  InsightPainPoint
} from '@/components/page-components/audiences/insights/getInsightDrawer';
import {
  INSIGHT_DRAWER_SECTION_KEYS,
  type InsightDrawerSectionKey
} from '@/components/page-components/audiences/insights/insightDrawerSections';
import type { InsightCompanyRow } from '@/components/page-components/audiences/insights/companies/getInsightCompanies';
import {
  UK_MAP_REGION_METRICS,
  type UkMapRegionMetric
} from '@/components/page-components/audiences/insights/ukMapRegions';
import { formatCompactGbp } from '@/utils/complianceTab/mapOrbisToComplianceCards';

const SECTOR_OVERLAYS = [
  'rgba(16,109,246,0.65)',
  'rgba(23,178,106,0.65)',
  'rgba(136,119,249,0.65)',
  'rgba(247,144,9,0.65)'
];

/** Figma sector photos. The figures tree has no image field. */
const SECTOR_IMAGE_BY_NAME: Record<string, string> = {
  'Professional & Business Services': '/images/audience-sectors/professional.png',
  'Construction & Infrastructure': '/images/audience-sectors/construction.png',
  'Manufacturing & Industrial': '/images/audience-sectors/manufacturing.png',
  'Travel, Hospitality & Leisure': '/images/audience-sectors/travel.png',
  'Real Estate & Built Environment': '/images/audience-sectors/real-estate.png',
  'Technology, Media & Telecoms': '/images/audience-sectors/tech.png',
  'Financial Services & Insurance': '/images/audience-sectors/finance.png',
  'Energy, Utilities & Cleantech': '/images/audience-sectors/energy.png',
  'Wholesale, Retail & Consumer': '/images/audience-sectors/retail.png',
  'Creative, Culture & Personal Services': '/images/audience-sectors/creative.png',
  'Healthcare, Life Sciences & Social Care':
    '/images/audience-sectors/healthcare.png',
  'Agriculture & Natural Resources': '/images/audience-sectors/agriculture.png',
  'Transport, Logistics & Supply Chain': '/images/audience-sectors/transport.png',
  'Automotive & Mobility': '/images/audience-sectors/automotive.png',
  'Education & Skills': '/images/audience-sectors/education.png',
  'Government, Defence & Public Sector': '/images/audience-sectors/government.png'
};

function sectorImageSrc(name: string | undefined): string {
  if (!name) return '';
  return SECTOR_IMAGE_BY_NAME[name] ?? '';
}

type TreeNode = {
  node_id?: string;
  name?: string;
  level?: number;
  parent_node_id?: string | null;
  pool?: number;
  our_data?: number;
  in_book_pct?: number | null;
  share_of_parent_pct?: number | null;
  ring_color?: string | null;
};

/** Audiences radial map rings. The figures tree has no colour of its own. */
const FIGMA_RING_COLOURS = [
  'var(--color-datavis-watermelon-500, #ff6595)',
  'var(--color-datavis-aqua-500, #00c9e8)',
  'var(--color-datavis-orange-500, #ff9f21)',
  'var(--color-datavis-purple-500, #8877f9)',
  'var(--color-brand-800, #0d286f)',
  'var(--color-brand-500, #086be0)'
] as const;

const UK_RING_COLOUR = 'var(--color-brand-500, #086be0)';

function ringColourFromResponse(node: TreeNode): string | undefined {
  const colour = node.ring_color?.trim();
  return colour ? colour : undefined;
}

function figmaRingColour(index: number): string {
  return FIGMA_RING_COLOURS[index % FIGMA_RING_COLOURS.length];
}

type TreeResponse = {
  total?: { pool?: number; our_data?: number; in_book_pct?: number | null };
  nodes?: TreeNode[];
};

type FiguresResponse = {
  nodes_label?: string;
  pool?: number;
  our_data?: number;
  in_book_pct?: number | null;
  contactable?: number;
  contactability?: {
    email_pct?: number | null;
    phone_pct?: number | null;
  };
  spend_per_route?: {
    gwp_gbp?: number;
    finance_gbp?: number;
    cash_gbp?: number;
    insights_gbp?: number;
    consulting_gbp?: number;
    marketing_gbp?: number;
  };
  last_synced?: string | null;
  figures_built_at?: string | null;
};

type MonetaryValue = { value?: number | null } | null;
type CountValue = { value?: number | null } | null;

export function formatExplorerCount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const absolute = Math.abs(Number(value));
  const sign = Number(value) < 0 ? '-' : '';
  if (absolute >= 1_000_000) {
    return `${sign}${trimNumber(absolute / 1_000_000)}m`;
  }
  if (absolute >= 1_000) {
    return `${sign}${trimNumber(absolute / 1_000)}k`;
  }
  return `${sign}${new Intl.NumberFormat('en-GB').format(absolute)}`;
}

export function formatExplorerPercent(
  value: number | null | undefined
): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const rounded = Math.round(Number(value) * 10) / 10;
  return `${rounded}%`;
}

function trimNumber(value: number): string {
  return value >= 10 ? String(Math.round(value)) : String(Math.round(value * 10) / 10);
}

function moneyAmount(value: MonetaryValue | number | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  return typeof value.value === 'number' ? value.value : null;
}

function countAmount(value: CountValue | number | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  return typeof value.value === 'number' ? value.value : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function menuLevel(level: number): InsightSegmentMenuItem['level'] {
  if (level <= 1) return 'sector';
  if (level === 2) return 'industry';
  return 'sub-industry';
}

function treeNodes(payload: unknown): TreeNode[] {
  const record = asRecord(payload);
  return Array.isArray(record?.nodes) ? (record.nodes as TreeNode[]) : [];
}

export function sectorCardsFromTree(payload: unknown): InsightSectorCard[] {
  return treeNodes(payload)
    .filter((node) => node.level === 1 && node.node_id && node.name)
    .map((node, index) => {
      const overlay = SECTOR_OVERLAYS[index % SECTOR_OVERLAYS.length];
      const coveragePercent =
        typeof node.in_book_pct === 'number' ? node.in_book_pct : 0;
      return {
        id: node.node_id as string,
        name: node.name as string,
        firms: formatExplorerCount(node.pool),
        coverage: formatExplorerPercent(node.in_book_pct),
        coveragePercent,
        imageSrc: sectorImageSrc(node.name),
        overlayColor: overlay,
        barColor: overlay,
        rowSize: 3
      };
    });
}

export function segmentMenuFromTree(payload: unknown): InsightSegmentMenuItem[] {
  return treeNodes(payload)
    .filter((node) => node.node_id && node.name)
    .map((node) => ({
      id: node.node_id as string,
      label: node.name as string,
      level: menuLevel(node.level ?? 1),
      parentId: node.parent_node_id ?? null,
      firms: formatExplorerCount(node.pool),
      profiles: formatExplorerCount(node.our_data),
      coverage: formatExplorerPercent(node.in_book_pct),
      share: formatExplorerPercent(node.share_of_parent_pct),
      sharePercent:
        typeof node.share_of_parent_pct === 'number'
          ? node.share_of_parent_pct
          : null
    }));
}

export function clusterFromTree(payload: unknown): ClusterDiagramNodeData {
  const record = asRecord(payload);
  const nodes = treeNodes(payload);
  const childrenOf = new Map<string | null, TreeNode[]>();
  nodes.forEach((node) => {
    const parent = node.parent_node_id ?? null;
    const list = childrenOf.get(parent) ?? [];
    list.push(node);
    childrenOf.set(parent, list);
  });

  const toCluster = (node: TreeNode, index: number): ClusterDiagramNodeData => ({
    id: node.node_id as string,
    label: node.name,
    value: formatExplorerCount(node.pool),
    caption:
      node.in_book_pct == null
        ? undefined
        : `${formatExplorerPercent(node.in_book_pct)} in book`,
    progress:
      typeof node.in_book_pct === 'number'
        ? Math.min(1, Math.max(0, node.in_book_pct / 100))
        : undefined,
    ringColor: ringColourFromResponse(node) ?? figmaRingColour(index),
    children: (childrenOf.get(node.node_id ?? '') ?? []).map(toCluster)
  });

  const roots = childrenOf.get(null) ?? nodes.filter((node) => node.level === 1);
  const total = asRecord(record?.total);
  return {
    id: 'uk',
    label: 'UK',
    value: formatExplorerCount(
      typeof total?.pool === 'number' ? total.pool : undefined
    ),
    caption:
      typeof total?.in_book_pct === 'number'
        ? `${formatExplorerPercent(total.in_book_pct)} in book`
        : undefined,
    ringColor: UK_RING_COLOUR,
    children: roots.filter((node) => node.node_id && node.name).map(toCluster)
  };
}

export function mapFiltersFromPayload(payload: unknown): InsightMapFilterConfig[] {
  const record = asRecord(payload);
  const filters = Array.isArray(payload)
    ? payload
    : Array.isArray(record?.filters)
      ? record.filters
      : [];
  return filters.flatMap((item) => {
    const filter = asRecord(item);
    if (!filter || typeof filter.id !== 'string' || typeof filter.label !== 'string') {
      return [];
    }
    const codes = Array.isArray(filter.codes)
      ? filter.codes.filter((code): code is string => typeof code === 'string')
      : [];
    return [{ id: filter.id, label: filter.label, codes }];
  });
}

function segmentFromFigures(
  payload: unknown,
  id: 'all' | 'customers',
  label: string
): InsightSegment {
  const figures = (asRecord(payload) ?? {}) as FiguresResponse;
  const spend = figures.spend_per_route ?? {};
  const firms = formatExplorerCount(figures.pool);
  return {
    id,
    label,
    title:
      id === 'customers'
        ? 'Customers in book'
        : figures.nodes_label || 'Total UK economy',
    supportText: `${firms} firms · ${formatExplorerPercent(figures.in_book_pct)} in book`,
    metrics: [
      {
        id: 'firms',
        label: 'Firms',
        value: firms,
        supportText: formatExplorerCount(figures.our_data)
      },
      {
        id: 'gwp',
        label: 'GWP',
        value: formatCompactGbp(spend.gwp_gbp),
        supportText: ''
      },
      {
        id: 'finance',
        label: 'Finance',
        value: formatCompactGbp(spend.finance_gbp),
        supportText: ''
      },
      {
        id: 'cash',
        label: 'Cash',
        value: formatCompactGbp(spend.cash_gbp),
        supportText: ''
      },
      {
        id: 'insights',
        label: 'Insights',
        value: formatCompactGbp(spend.insights_gbp),
        supportText: ''
      },
      {
        id: 'consult',
        label: 'Consult',
        value: formatCompactGbp(spend.consulting_gbp),
        supportText: ''
      },
      {
        id: 'mktg',
        label: 'MKTG',
        value: formatCompactGbp(spend.marketing_gbp),
        supportText: ''
      }
    ]
  };
}

export function segmentsFromFigures(
  economy: unknown,
  customers: unknown | null
): InsightSegment[] {
  const segments = [segmentFromFigures(economy, 'all', 'All')];
  if (customers) segments.push(segmentFromFigures(customers, 'customers', 'Customers'));
  return segments;
}

const ICP_TITLES: Record<string, string> = {
  bullseye: 'Bullseye',
  near_fit: 'Near fit',
  long_tail: 'Long tail'
};

export function drawerFromExplorerParts(input: {
  nodeId: string;
  label?: string;
  figures: unknown;
  scorecard?: unknown | null;
  players?: unknown | null;
  painPoints?: unknown | null;
  horizon?: unknown | null;
  icpTiers?: unknown | null;
  coverage?: unknown | null;
}): InsightDrawerPayload {
  const figures = (asRecord(input.figures) ?? {}) as FiguresResponse;
  const pool = figures.pool ?? 0;
  const share = (part: number | undefined) =>
    pool > 0 && part != null ? Math.round((part / pool) * 100) : 0;
  const scorecard = asRecord(input.scorecard);
  const players = asRecord(input.players);
  const pain = asRecord(input.painPoints);
  const horizon = asRecord(input.horizon);
  const horizonPage = horizonFromPayload(horizon);
  const painPointsPage = painPointsFromPayload(pain);
  const icp = asRecord(input.icpTiers);
  const coverage = asRecord(input.coverage);
  const shares = asRecord(coverage?.shares);
  const emailShare =
    typeof shares?.email === 'number' ? shares.email : figures.in_book_pct;

  const funnel = [
    {
      id: 'pool',
      label: 'Pool',
      value: formatExplorerCount(figures.pool),
      percent: 100,
      tone: 'navy' as const
    },
    {
      id: 'our-data',
      label: 'Our data',
      value: formatExplorerCount(figures.our_data),
      percent: share(figures.our_data),
      tone: 'blue' as const
    },
    {
      id: 'contactable',
      label: 'Contactable',
      value: formatExplorerCount(figures.contactable),
      percent: share(figures.contactable),
      tone: 'aqua' as const
    }
  ];

  const contactability = [
    figures.contactability?.email_pct,
    figures.contactability?.phone_pct
  ]
    .map((percent, index) =>
      typeof percent === 'number'
        ? {
            id: index === 0 ? 'email' : 'phone',
            label: index === 0 ? 'Email' : 'Phone',
            percent,
            tone: index === 0 ? ('purple' as const) : ('aqua' as const)
          }
        : null
    )
    .filter((row): row is NonNullable<typeof row> => row != null);

  return {
    nodeId: input.nodeId,
    title: input.label?.trim() || figures.nodes_label || input.nodeId,
    subtitle: `${formatExplorerCount(figures.pool)} firms · ${formatExplorerPercent(figures.in_book_pct)} in book`,
    freshness: formatWhen(figures.figures_built_at ?? figures.last_synced),
    completeness: formatExplorerPercent(
      typeof emailShare === 'number' ? emailShare : null
    ),
    funnel,
    contactability,
    scorecard: Array.isArray(scorecard?.tiles)
      ? scorecard.tiles.flatMap((tile) => {
          const record = asRecord(tile);
          if (!record) return [];
          return [
            {
              value: tileDisplay(record),
              hint: typeof record.label === 'string' ? record.label : ''
            }
          ];
        })
      : [],
    players: Array.isArray(players?.players)
      ? players.players.flatMap((player) => {
          const record = asRecord(player);
          if (!record || typeof record.name !== 'string') return [];
          const income = moneyAmount(record.income as MonetaryValue);
          return [
            {
              id: String(record.master_company_id ?? record.name),
              name: record.name,
              income: formatCompactGbp(income),
              employees: countAmount(record.employees as CountValue) ?? 0,
              incomeMillions: income == null ? 0 : income / 1_000_000,
              products:
                typeof record.products_held === 'number'
                  ? String(record.products_held)
                  : undefined,
              inBook: record.is_customer === true
            }
          ];
        })
      : [],
    painPoints: painPointsPage.points,
    painPointsPending: painPointsPage.pending,
    horizon: horizonPage.items,
    horizonTotal: horizon ? horizonPage.total : undefined,
    horizonCoverage: horizonPage.coverage,
    products: [],
    productGroup: figures.nodes_label || '',
    icpTiers: Array.isArray(icp?.tiers)
      ? icp.tiers.flatMap((tier) => {
          const record = asRecord(tier);
          if (!record || typeof record.tier !== 'string') return [];
          return [
            {
              id: record.tier,
              title: ICP_TITLES[record.tier] ?? record.tier,
              detail: `${formatExplorerCount(typeof record.companies === 'number' ? record.companies : null)} companies · ${formatExplorerCount(typeof record.existing_customers === 'number' ? record.existing_customers : null)} already customers`,
              action: typeof record.approach === 'string' ? record.approach : '',
              actionTone:
                record.tier === 'bullseye'
                  ? ('solid' as const)
                  : record.tier === 'near_fit'
                    ? ('brand' as const)
                    : ('neutral' as const)
            }
          ];
        })
      : [],
    sections: [...INSIGHT_DRAWER_SECTION_KEYS]
  };
}

function tileDisplay(tile: Record<string, unknown>): string {
  if (typeof tile.value === 'number') {
    return tile.unit ? `${tile.value} ${String(tile.unit)}` : String(tile.value);
  }
  if (typeof tile.value === 'string') return tile.value;
  return '—';
}

function formatWhen(value: unknown): string {
  if (typeof value !== 'string' || !value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' }).format(date);
}

export function companiesPageFromPayload(
  payload: unknown,
  fallback: { nodeId: string; title?: string; caption?: string }
): InsightCompaniesPageData {
  const record = asRecord(payload) ?? {};
  const node = asRecord(record.node);
  const title =
    fallback.title ||
    (typeof node?.name === 'string' ? node.name : fallback.nodeId);
  const companies = Array.isArray(record.companies)
    ? record.companies.flatMap((company) => companyRow(company))
    : [];
  return {
    nodeId: typeof node?.id === 'string' ? node.id : fallback.nodeId,
    title,
    heading: title,
    subtitle: fallback.caption || `${formatExplorerCount(typeof record.total === 'number' ? record.total : companies.length)} companies`,
    metrics: [
      {
        id: 'companies',
        label: 'Companies',
        value: formatExplorerCount(
          typeof record.total === 'number' ? record.total : companies.length
        ),
        supportText: ''
      }
    ],
    companies
  };
}

function companyRow(value: unknown): InsightCompanyRow[] {
  const record = asRecord(value);
  if (!record || typeof record.name !== 'string') return [];
  const income = moneyAmount(record.income as MonetaryValue);
  const spend = asRecord(record.spend);
  const icp = asRecord(record.icp);
  const warmth = asRecord(record.warmth);
  const id = String(
    record.permanent_company_id ?? record.master_company_id ?? record.name
  );
  return [
    {
      id,
      name: record.name,
      pipelines: '',
      icp:
        typeof icp?.tier === 'string'
          ? `${ICP_TITLES[icp.tier] ?? icp.tier}${typeof icp.score === 'number' ? ` · ${icp.score}` : ''}`
          : '',
      lead: typeof warmth?.band === 'string' ? warmth.band : '',
      employees: formatExplorerCount(countAmount(record.employees as CountValue)),
      income: formatCompactGbp(income),
      incomeValue: income ?? 0,
      gwp: formatCompactGbp(moneyAmount(record.gwp as MonetaryValue)),
      fin: formatCompactGbp(
        typeof spend?.finance === 'number' ? spend.finance : null
      ),
      cash: formatCompactGbp(typeof spend?.cash === 'number' ? spend.cash : null),
      products:
        typeof record.products_held === 'number' ? String(record.products_held) : '',
      isCustomer: record.is_customer === true,
      opportunities: Array.isArray(record.opportunities)
        ? record.opportunities.flatMap((item, index) => {
            const opportunity = asRecord(item);
            if (!opportunity || typeof opportunity.label !== 'string') return [];
            const current =
              typeof opportunity.current_per_year === 'number'
                ? opportunity.current_per_year
                : null;
            const max =
              typeof opportunity.opportunity_per_year === 'number'
                ? opportunity.opportunity_per_year
                : null;
            return [
              {
                id: `${id}-${index}`,
                label: opportunity.label,
                current: formatCompactGbp(current),
                max: formatCompactGbp(max),
                percent:
                  current != null && max
                    ? Math.max(0, Math.min(100, Math.round((current / max) * 100)))
                    : 0
              }
            ];
          })
        : [],
      pipelineLinks: []
    }
  ];
}

export function pipelineResultFromPayload(
  payload: unknown
): InsightBulkPipelineResponse {
  const record = asRecord(payload);
  if (!record) {
    return { status: 'ERROR', message: 'The pipeline add could not be read' };
  }
  const added = typeof record.added === 'number' ? record.added : 0;
  if (record.status === 'failed') {
    return {
      status: 'ERROR',
      message:
        typeof record.failed_reason === 'string'
          ? record.failed_reason
          : 'The pipeline add failed',
      added_count: added
    };
  }
  if (record.status === 'running') {
    return {
      status: 'SUCCESS',
      message: 'Adding companies to the pipeline',
      added_count: added
    };
  }
  const name = typeof record.pipeline_name === 'string' ? record.pipeline_name : '';
  return {
    status: 'SUCCESS',
    message: name
      ? `Added ${added} to ${name}`
      : `Added ${added} companies`,
    added_count: added
  };
}

export function overlayAreaFigures(
  payload: unknown,
  regions: UkMapRegionMetric[] = UK_MAP_REGION_METRICS
): UkMapRegionMetric[] {
  const record = asRecord(payload);
  const areas = Array.isArray(record?.areas) ? record.areas : [];
  const byCode = new Map<string, Record<string, unknown>>();
  areas.forEach((area) => {
    const item = asRecord(area);
    if (item && typeof item.code === 'string') {
      byCode.set(item.code.toUpperCase(), item);
    }
  });
  return regions.map((region) => {
    const live = byCode.get(region.postcode.toUpperCase());
    if (!live) {
      return { ...region, firms: '—', coverage: '—', coveragePercent: 0 };
    }
    const coveragePercent =
      typeof live.in_book_pct === 'number' ? live.in_book_pct : 0;
    return {
      ...region,
      label: typeof live.name === 'string' ? live.name : region.label,
      firms: formatExplorerCount(typeof live.pool === 'number' ? live.pool : null),
      coverage: formatExplorerPercent(
        typeof live.in_book_pct === 'number' ? live.in_book_pct : null
      ),
      coveragePercent
    };
  });
}

export function isPipelineUuid(value: string | null | undefined): value is string {
  return Boolean(
    value &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value
      )
  );
}

/** Insights setting ids for each drawer section (`explorer.drawer_order`). */
const DRAWER_SECTION_SETTING_IDS: Record<InsightDrawerSectionKey, string> = {
  audienceBreakdown: 'funnel',
  contactability: 'contact',
  macroeconomicScorecard: 'macro',
  bigPlayers: 'players',
  painPoints: 'pain',
  horizonScanning: 'horizon',
  productMapping: 'products',
  icpTiers: 'icp'
};

export const DRAWER_ORDER_SETTING_KEY = 'explorer.drawer_order';

/** Reads the saved order, ignoring ids the portal doesn't know. Falls back to the default order. */
export function drawerOrderFromSettings(
  payload: unknown
): InsightDrawerSectionKey[] {
  const settings = asRecord(payload)?.settings;
  const entry = Array.isArray(settings)
    ? settings.map(asRecord).find((s) => s?.key === DRAWER_ORDER_SETTING_KEY)
    : null;
  return drawerOrderFromSettingValue(entry?.value);
}

export function drawerOrderFromSettingValue(
  value: unknown
): InsightDrawerSectionKey[] {
  if (!Array.isArray(value)) return [...INSIGHT_DRAWER_SECTION_KEYS];
  const byId = Object.fromEntries(
    Object.entries(DRAWER_SECTION_SETTING_IDS).map(([key, id]) => [id, key])
  ) as Record<string, InsightDrawerSectionKey>;
  const order = value
    .map((id) => byId[String(id)])
    .filter((key): key is InsightDrawerSectionKey => Boolean(key));
  return order.length ? order : [...INSIGHT_DRAWER_SECTION_KEYS];
}

export function drawerOrderToSettingValue(
  order: InsightDrawerSectionKey[]
): string[] {
  return order.map((key) => DRAWER_SECTION_SETTING_IDS[key]);
}

const HORIZON_KIND: Record<
  string,
  { tag: string; tone: InsightHorizonItem['tone'] }
> = {
  news: { tag: 'News', tone: 'brand' },
  regulatory: { tag: 'Regulatory', tone: 'purple' },
  risk: { tag: 'Risk', tone: 'error' }
};

export function horizonFromPayload(payload: unknown): InsightHorizonPage {
  const record = asRecord(payload);
  const rows = record?.items;
  const items = Array.isArray(rows)
    ? rows.flatMap((item): InsightHorizonItem[] => {
        const row = asRecord(item);
        if (!row || typeof row.headline !== 'string') return [];
        const kind = typeof row.kind === 'string' ? row.kind : '';
        const precision = row.published_precision;
        return [
          {
            id: String(row.item_id ?? row.headline),
            title: row.headline,
            source: typeof row.source === 'string' ? row.source : '',
            tag: HORIZON_KIND[kind]?.tag ?? kind,
            tone: HORIZON_KIND[kind]?.tone ?? 'brand',
            url: typeof row.url === 'string' ? row.url : undefined,
            publishedAt:
              typeof row.published_at === 'string' ? row.published_at : null,
            publishedPrecision:
              precision === 'time' || precision === 'day'
                ? precision
                : 'unknown'
          }
        ];
      })
    : [];
  return {
    items,
    total: typeof record?.total === 'number' ? record.total : items.length,
    coverage:
      typeof record?.coverage === 'string' ? record.coverage : undefined,
    staleImport: record?.stale_import === true
  };
}

const OPPORTUNITY_TONE: Record<string, InsightPainPoint['opportunityTone']> = {
  very_high: 'green',
  high: 'blue',
  medium: 'grey'
};

/** "very_common" -> "Very common" */
function bandLabel(value: unknown): string {
  if (typeof value !== 'string' || !value) return '—';
  const words = value.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Pain points for the drawer. Prevalence, opportunity and fit are model estimates:
 * only their bands are shown, never prevalence.value as a percentage.
 */
export function painPointsFromPayload(payload: unknown): {
  points: InsightPainPoint[];
  pending: string[];
} {
  const record = asRecord(payload);
  const rows = record?.points;
  const points = Array.isArray(rows)
    ? rows.flatMap((row): InsightPainPoint[] => {
        const point = asRecord(row);
        if (!point) return [];
        const label = typeof point.label === 'string' ? point.label : '';
        const fullTitle = typeof point.title === 'string' ? point.title : '';
        const title = label || fullTitle;
        if (!title) return [];
        const opportunity = asRecord(point.opportunity);
        const firms =
          typeof opportunity?.firms === 'number' ? opportunity.firms : null;
        const prospects =
          opportunity?.basis === 'contactable'
            ? 'contactable firms'
            : 'ICP prospects';
        return [
          {
            id: String(point.theme_key ?? title),
            title,
            detailTitle: label && fullTitle !== label ? fullTitle : undefined,
            prevalence: bandLabel(asRecord(point.prevalence)?.band),
            severity: bandLabel(point.severity),
            opportunity: bandLabel(opportunity?.band),
            opportunityTone:
              OPPORTUNITY_TONE[String(opportunity?.band)] ?? 'grey',
            opportunityHint:
              firms === null
                ? undefined
                : `About ${firms.toLocaleString('en-GB')} ${prospects}`,
            body: typeof point.text === 'string' ? point.text : undefined
          }
        ];
      })
    : [];
  const pending = Array.isArray(record?.pending_nodes)
    ? (record?.pending_nodes as unknown[]).map(String)
    : [];
  return { points, pending };
}
